// Shared rich-text layout: turns parseNoteHtml's Block[] into VisualLine[]
// ready to paint on a canvas — word-wrapped, list-indented, one line per
// row. Pure + unit-testable: canvas measurement is injected as `measureRun`
// so this file never touches a 2D context itself. Consumed by drawNote
// (Canvas.jsx) and, later, the markdown-file element.
//
// VisualLine = {
//     runs: [{ text, x, width, bold?, italic?, underline?, strike?, href? }],
//     width,        // line's total content width (includes the list indent)
//     blockType,    // 'paragraph' | 'bullet' | 'numbered'
//     isBlockStart, // true on a block's first visual line, false on wraps
//     listIndex,    // 1-based position within a contiguous 'numbered' run, else null
// }

export const LIST_INDENT = 23.5;
export const BULLET_DOT_RADIUS = 3.7;
export const BULLET_DOT_X = 8;

const MARK_KEYS = ['bold', 'italic', 'underline', 'strike'];

// Present-only-when-true, same convention as parseNoteHtml's Run.
const pickMarks = (run) => {
    const marks = {};
    if (run.bold) marks.bold = true;
    if (run.italic) marks.italic = true;
    if (run.underline) marks.underline = true;
    if (run.strike) marks.strike = true;
    if (run.href) marks.href = run.href;
    return marks;
};

const marksEqual = (a, b) =>
    MARK_KEYS.every((k) => !!a[k] === !!b[k]) && (a.href || null) === (b.href || null);

// measureRun only cares about bold/italic (the metrics-affecting marks) —
// pare down whatever mark bag we're carrying to that shape at the boundary.
const measureAtom = (text, marks, measureRun) =>
    measureRun(text, { bold: !!marks.bold, italic: !!marks.italic });

// Flattens a block's runs into word/space "groups" in source order. A group
// is either one whitespace atom, or one or more word atoms glued together
// with no whitespace between them (i.e. a single visual word that may span
// a run boundary, e.g. "<b>te</b>st") — wrapping must not split a group
// apart except via the character-level hard-break fallback.
const buildGroups = (runs, measureRun) => {
    const groups = [];
    let openWord = null;
    for (const run of runs) {
        const marks = pickMarks(run);
        if (!run.text) continue;
        const pieces = run.text.split(/(\s+)/).filter((s) => s !== '');
        for (const piece of pieces) {
            const isSpace = /^\s/.test(piece);
            const atom = { text: piece, marks, width: measureAtom(piece, marks, measureRun) };
            if (isSpace) {
                groups.push({ isSpace: true, atoms: [atom], width: atom.width });
                openWord = null;
            } else if (openWord) {
                openWord.atoms.push(atom);
                openWord.width += atom.width;
            } else {
                openWord = { isSpace: false, atoms: [atom], width: atom.width };
                groups.push(openWord);
            }
        }
    }
    return groups;
};

// Greedy word-wrap over one block's groups into rows of atoms (pre-indent,
// pre-merge). Mirrors wrapNoteText's algorithm, generalized to carry marks
// and to keep multi-run words intact except when hard-breaking.
const wrapGroupsIntoAtomRows = (groups, availWidth, measureRun) => {
    const rows = [];
    let current = [];
    let x = 0;

    const flush = () => {
        while (current.length && /^\s/.test(current[current.length - 1].text)) {
            x -= current.pop().width;
        }
        rows.push(current);
        current = [];
        x = 0;
    };

    const place = (atoms) => {
        for (const atom of atoms) { current.push(atom); x += atom.width; }
    };

    for (const group of groups) {
        if (group.isSpace) {
            if (current.length === 0) continue; // drop leading whitespace on a fresh line
            if (x + group.width <= availWidth) place(group.atoms);
            else flush(); // space doesn't fit either: wrap now, drop the space
            continue;
        }
        if (x + group.width <= availWidth) { place(group.atoms); continue; }
        if (current.length > 0) flush();
        if (group.width <= availWidth) { place(group.atoms); continue; }

        // Hard-break: the group alone is wider than the whole box — break it
        // character by character (crossing atom/run boundaries as needed).
        for (const atom of group.atoms) {
            let chunk = '';
            for (const ch of atom.text) {
                const trial = chunk + ch;
                if (chunk !== '' && x + measureAtom(trial, atom.marks, measureRun) > availWidth) {
                    const w = measureAtom(chunk, atom.marks, measureRun);
                    current.push({ text: chunk, marks: atom.marks, width: w });
                    x += w;
                    flush();
                    chunk = ch;
                } else {
                    chunk = trial;
                }
            }
            if (chunk !== '') {
                const w = measureAtom(chunk, atom.marks, measureRun);
                current.push({ text: chunk, marks: atom.marks, width: w });
                x += w;
            }
        }
    }
    flush();
    return rows;
};

// Merges adjacent same-mark atoms into positioned runs, starting at `indent`.
const atomsToRuns = (atoms, indent) => {
    const runs = [];
    let x = indent;
    for (const atom of atoms) {
        const prev = runs[runs.length - 1];
        if (prev && marksEqual(prev, atom.marks)) {
            prev.text += atom.text;
            prev.width += atom.width;
        } else {
            runs.push({ text: atom.text, x, width: atom.width, ...atom.marks });
        }
        x += atom.width;
    }
    if (!runs.length) runs.push({ text: '', x: indent, width: 0 });
    return runs;
};

export function layoutRichBlocks(blocks, { maxWidth, measureRun }) {
    const lines = [];
    let numberedRun = 0;

    for (const block of blocks) {
        const isList = block.type === 'bullet' || block.type === 'numbered';
        const indent = isList ? LIST_INDENT : 0;
        const availWidth = Math.max(maxWidth - indent, 0);

        let listIndex = null;
        if (block.type === 'numbered') {
            numberedRun += 1;
            listIndex = numberedRun;
        } else {
            numberedRun = 0;
        }

        const groups = buildGroups(block.runs, measureRun);
        const atomRows = groups.length
            ? wrapGroupsIntoAtomRows(groups, availWidth, measureRun)
            : [[]];

        atomRows.forEach((atoms, i) => {
            const runs = atomsToRuns(atoms, indent);
            const last = runs[runs.length - 1];
            lines.push({
                runs,
                width: last.x + last.width,
                blockType: block.type,
                isBlockStart: i === 0,
                listIndex,
            });
        });
    }

    return lines;
}

// Pure world-coord geometry for drawNote's canvas link-hover zones (spec
// 8b). Consumes the SAME lines this module produces and mirrors drawNote's
// own per-line paint math exactly (lineY/rowTop/lineX offset/clip break) so
// a zone only ever exists where a link run is actually painted:
//   - `x`/`firstBaseline`/`lineHeight`/`fontSize`/`innerWidth`/
//     `alignmentHorizontal` are the same values drawNote's paint loop uses
//     (x is the note's already-padded text-left origin, i.e. `x +
//     NOTE_PADDING_X` in Canvas.jsx, not the note's own x).
//   - `clipBottom` is drawNote's clip boundary (`y + height - NOTE_PADDING_Y`
//     in world coords).
// A line is "fully visible" when its whole row box (rowTop..rowBottom, where
// rowBottom = rowTop + lineHeight) sits at/above clipBottom. The partially-
// cut boundary row — which drawNote still paints as a visual sliver inside
// its ctx.clip — is deliberately EXCLUDED here: simplest correct rule, a
// link is only hoverable when its entire line is on-screen. Once a line's
// row top itself is past clipBottom, every following line is further down
// still, so iteration stops (mirrors drawNote's own `break`).
export function collectNoteLinkZones(lines, {
    x, firstBaseline, lineHeight, fontSize, innerWidth, alignmentHorizontal, clipBottom,
}) {
    const zones = [];
    for (let i = 0; i < lines.length; i++) {
        const lineY = firstBaseline + i * lineHeight;
        const rowTop = lineY - fontSize;
        if (rowTop > clipBottom) break;
        const rowBottom = rowTop + lineHeight;
        if (rowBottom > clipBottom) continue; // partial boundary row: not hoverable

        const line = lines[i];
        let lineOffsetX = 0;
        if (alignmentHorizontal === 'right') lineOffsetX = innerWidth - line.width;
        else if (alignmentHorizontal === 'center') lineOffsetX = (innerWidth - line.width) / 2;
        const lineX = x + lineOffsetX;

        for (const run of line.runs) {
            if (!run.href || !run.text) continue;
            zones.push({ x: lineX + run.x, y: rowTop, w: run.width, h: lineHeight, href: run.href });
        }
    }
    return zones;
}
